// src/pages/UnsoldTicketAppraisal/index.tsx
// Mục 22/23, REQUIRE_FIXBUGS.md — Duyệt thu hồi vé ế. Đây là LUỒNG CHUẨN: đại lý tạo phiếu thu hồi
// (TicketRecall) → nội bộ "Xác nhận thu hồi" (Confirmed) → màn này Duyệt/Từ chối phiếu đã
// Confirmed (Confirmed -> Approved/Rejected, lưu trực tiếp trên TicketRecall — không tách bảng
// riêng). Thay thế hoàn toàn luồng cũ dựa trên TicketReturnReconciliation (không còn dữ liệu vì
// app đại lý không dùng UnsoldTicketReturnRequest).
// Vai trò: Nhân viên Thẩm định / Ban Lãnh đạo. Layout bám theo trang /issuance-plans.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Input, Label, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import BreadCrumb from 'Components/Common/BreadCrumb';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { AgentMap, buildAgentMap, loadAgentsWithCache } from 'helpers/agentLookup';
import { IAgent } from 'slices/agentManagement/types';
import {
  approveTicketRecall,
  fetchAppraisalRecallById,
  fetchAppraisalRecalls,
  rejectTicketRecall,
} from 'slices/ticketRecallAppraisal/thunk';
import {
  clearFilters,
  closeModal,
  openModal,
  setFilters,
  setPage,
  setSelectedItem,
} from 'slices/ticketRecallAppraisal/reducer';
import { TICKET_RECALL_STATUS, TicketRecall } from 'pages/TicketRecall/types';
import { useBrandingTitle } from '../../hooks';

import AppraisalList from './components/AppraisalList';
import RecallAppraisalDetailModal from './modals/RecallAppraisalDetailModal';
import AppraisalConfirmModal, { AppraisalActionType } from './modals/AppraisalConfirmModal';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('unsold_ticket_appraisal');

// Chỉ 3 trạng thái liên quan tới màn này (Draft/Cancelled không cần duyệt). Nhãn "Chờ thẩm định"
// khớp mục 8.d, README_UPDATE.md (đồng bộ với TICKET_RECALL_STATUS_LABELS). Mặc định hiện "Tất cả
// trạng thái" (gộp cả 3) để role admin thấy toàn bộ danh sách, đã thẩm định hoặc chưa — không chỉ
// riêng phiếu đang chờ xử lý.
const APPRAISAL_STATUSES = [
  TICKET_RECALL_STATUS.Confirmed,
  TICKET_RECALL_STATUS.Approved,
  TICKET_RECALL_STATUS.Rejected,
];
const APPRAISAL_STATUS_OPTIONS = [
  { value: 'All', label: st('tat_ca_trang_thai_label') },
  { value: String(TICKET_RECALL_STATUS.Confirmed), label: st('cho_tham_dinh_label') },
  { value: String(TICKET_RECALL_STATUS.Approved), label: st('da_duyet_label') },
  { value: String(TICKET_RECALL_STATUS.Rejected), label: st('tu_choi_button') },
];

const UnsoldTicketAppraisal: React.FC = () => {
  useBrandingTitle(st('duyet_thu_hoi_ve_e_text'));

  const dispatch = useDispatch();
  const location = useLocation();

  const {
    list,
    selectedItem,
    pagination,
    filters,
    loading,
    loadingDetail,
    submitting,
    modalState,
  } = useSelector((state: any) => state.TicketRecallAppraisal);

  // DTO chỉ có agentId — map sang mã/tên qua helpers/agentLookup (cache dùng chung nhiều trang)
  const [agents, setAgents] = useState<IAgent[]>([]);
  const agentMap: AgentMap = useMemo(() => buildAgentMap(agents), [agents]);
  const [appraisalAction, setAppraisalAction] = useState<AppraisalActionType>('approve');

  useEffect(() => {
    loadAgentsWithCache().then(setAgents);
  }, []);

  const loadRecalls = useCallback(async () => {
    const isAllStatuses = !filters.status || filters.status === 'All';
    const result = await dispatch(
      fetchAppraisalRecalls({
        status: isAllStatuses ? undefined : filters.status,
        statuses: isAllStatuses ? APPRAISAL_STATUSES : undefined,
        planId: filters.planId || undefined,
        agentId: filters.agentId || undefined,
        pageIndex: pagination.currentPage,
        pageSize: pagination.pageSize,
      }) as any,
    );
    if (!fetchAppraisalRecalls.fulfilled.match(result)) {
      toast.error((result.payload as string) || st('khong_the_tai_danh_sach_phieu_message'));
    }
  }, [dispatch, filters, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    loadRecalls();
  }, [loadRecalls]);

  const handleViewDetail = async (item: TicketRecall) => {
    dispatch(setSelectedItem(item));
    dispatch(openModal('detail'));
    await dispatch(fetchAppraisalRecallById(item.id) as any);
  };

  const handleApproveClick = (item: TicketRecall) => {
    setAppraisalAction('approve');
    dispatch(setSelectedItem(item));
    dispatch(openModal('approve'));
  };

  const handleRejectClick = (item: TicketRecall) => {
    setAppraisalAction('reject');
    dispatch(setSelectedItem(item));
    dispatch(openModal('reject'));
  };

  const handleAppraisalSubmit = async (comment?: string) => {
    if (!selectedItem?.id) return;
    const targetId = selectedItem.id;

    if (appraisalAction === 'approve') {
      const result = await dispatch(approveTicketRecall({ id: targetId, comment }) as any);
      if (approveTicketRecall.fulfilled.match(result)) {
        // Mục 8.d, README_UPDATE.md — hiện đúng số liệu đối soát thật trả về từ backend
        // (ApproveTicketRecallResultDto), không tự bịa số.
        const payload = result.payload as
          | { reconciledLineCount?: number; reconciledQuantity?: number }
          | undefined;
        const lineCount = payload?.reconciledLineCount;
        const quantity = payload?.reconciledQuantity;
        toast.success(
          lineCount !== undefined && quantity !== undefined
            ? st('da_duyet_va_doi_soat_xong_message', { line_count: lineCount, value: quantity.toLocaleString('vi-VN') })
            : st('duyet_phieu_thu_hoi_thanh_cong_message'),
        );
        // Mục 13.b, README_UPDATE.md — phiếu phải VẪN Ở LẠI danh sách, chỉ đổi badge trạng thái tại
        // chỗ. Nạp lại đúng phiếu này (không loadRecalls() cả trang, sẽ bị filter trạng thái cũ loại
        // mất khỏi kết quả) — xem reducer fetchAppraisalRecallById.fulfilled.
        dispatch(fetchAppraisalRecallById(targetId) as any);
      } else {
        toast.error((result.payload as string) || st('khong_the_duyet_phieu_thu_hoi_message'));
      }
      return;
    }

    const result = await dispatch(rejectTicketRecall({ id: targetId, comment: comment || '' }) as any);
    if (rejectTicketRecall.fulfilled.match(result)) {
      toast.success(st('da_tu_choi_phieu_thu_hoi_message'));
      dispatch(fetchAppraisalRecallById(targetId) as any);
    } else {
      toast.error((result.payload as string) || st('khong_the_tu_choi_phieu_thu_message'));
    }
  };

  const isActionModalOpen = modalState.approve || modalState.reject;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('duyet_thu_hoi_ve_e_text')} pageTitle={st('thu_hoi_ve_e_text')} />

          <Card id="ticketRecallAppraisalList">
            <CardHeader className="card-header border-0">
              <Row className="align-items-center gy-3">
                <div className="col-sm">
                  <h5 className="card-title mb-0">{st('danh_sach_phieu_thu_hoi_cho_title')}</h5>
                </div>
                <div className="col-sm-auto">
                  <button
                    type="button"
                    className="btn btn-soft-secondary"
                    disabled={loading}
                    onClick={() => dispatch(clearFilters())}
                  >
                    <i className="ri-refresh-line align-bottom me-1"></i>
                    {st('xoa_loc_button')}
                  </button>
                </div>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              <Row className="g-3 mb-3 align-items-end mb-5">
                <Col md={4}>
                  <Label className="form-label">{st('trang_thai_column')}</Label>
                  <Input
                    type="select"
                    className="form-select"
                    value={filters.status}
                    data-action-code="filter-input-status"
                    data-action-name="Lọc theo trạng thái"
                    disabled={!PageActionPermission.canEnable(location.pathname, 'filter-input-status')}
                    onChange={(event) => dispatch(setFilters({ status: event.target.value }))}
                  >
                    {APPRAISAL_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={4}>
                  <Label className="form-label">{st('dai_ly_label')}</Label>
                  <Input
                    type="select"
                    className="form-select"
                    value={filters.agentId}
                    data-action-code="filter-input-agent"
                    data-action-name="Lọc theo đại lý"
                    disabled={!PageActionPermission.canEnable(location.pathname, 'filter-input-agent')}
                    onChange={(event) => dispatch(setFilters({ agentId: event.target.value }))}
                  >
                    <option value="">{st('tat_ca_dai_ly_option')}</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.code} - {agent.name}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={4} className="text-md-end">
                  <span className="text-muted">
                    {st('tong_text')} <span className="fw-semibold">{pagination.totalItems}</span> {st('phieu_text')}
                  </span>
                </Col>
              </Row>

              <AppraisalList
                items={list}
                pagination={pagination}
                agentMap={agentMap}
                loading={loading}
                onViewDetail={handleViewDetail}
                onApprove={handleApproveClick}
                onReject={handleRejectClick}
              />

              <AgentPagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
                pageSize={pagination.pageSize}
                onPageChange={(page) => dispatch(setPage(page))}
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      {modalState.detail && (
        <RecallAppraisalDetailModal
          isOpen={modalState.detail}
          item={selectedItem}
          agentMap={agentMap}
          loading={loadingDetail}
          onClose={() => {
            dispatch(closeModal('detail'));
            dispatch(setSelectedItem(null));
          }}
        />
      )}

      {isActionModalOpen && (
        <AppraisalConfirmModal
          isOpen={isActionModalOpen}
          action={appraisalAction}
          item={selectedItem}
          agentMap={agentMap}
          submitting={submitting}
          onClose={() => {
            dispatch(closeModal(appraisalAction));
            dispatch(setSelectedItem(null));
          }}
          onSubmit={handleAppraisalSubmit}
        />
      )}
    </React.Fragment>
  );
};

export default UnsoldTicketAppraisal;
